import { createContext, useCallback, useContext, useEffect, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from "react";
import { initials } from "@/lib/format";
import { mediaUrl } from "@/lib/media";

export function cx(...a: (string | false | null | undefined)[]) {
  return a.filter(Boolean).join(" ");
}

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & { tone?: "primary" | "dark" | "light" | "danger" | "ghost"; size?: "sm" | "md" | "lg" };
export function Button({ tone = "light", size = "md", className, ...p }: BtnProps) {
  const t = {
    primary: "bg-brand text-white hover:bg-brand-dark border border-brand",
    dark: "bg-ink text-white hover:bg-graphite border border-ink",
    light: "bg-white text-ink border border-line-2 hover:border-ink",
    danger: "bg-white text-stop border border-stop hover:bg-stop hover:text-white",
    ghost: "bg-transparent text-muted hover:text-ink border border-transparent",
  }[tone];
  const s = { sm: "h-10 px-3 text-[13px]", md: "h-12 px-4 text-sm", lg: "h-14 px-5 text-base" }[size];
  return <button type="button" {...p} className={cx("inline-flex items-center justify-center gap-2 font-bold transition-colors disabled:opacity-45", t, s, className)} />;
}

export function Field({ label, hint, children, className }: { label: string; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={cx("flex flex-col gap-1.5", className)}>
      <span className="text-[13px] font-semibold">{label}</span>
      {children}
      {hint ? <span className="text-xs text-slate-500">{hint}</span> : null}
    </label>
  );
}
export const inputCls = "h-12 w-full border border-line-2 bg-white px-3 text-[15px] outline-none focus:border-ink";
export function Input(p: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...p} className={cx(inputCls, p.className)} />;
}
export function Select(p: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...p} className={cx(inputCls, "pr-8", p.className)} />;
}

export function Modal({ title, sub, onClose, children, footer, wide, xl }: { title: ReactNode; sub?: ReactNode; onClose?: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean; xl?: boolean }) {
  useEffect(() => {
    if (!onClose) return;
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/55 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div role="dialog" aria-modal="true" className={cx("ui-modal flex max-h-[92vh] w-full flex-col bg-white shadow-[0_30px_80px_rgba(0,0,0,0.35)]", xl ? "max-w-6xl" : wide ? "max-w-3xl" : "max-w-lg")}>
        <div className="ui-modal-head flex items-start gap-3 px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-extrabold">{title}</h2>
            {sub ? <p className="ui-modal-sub mt-0.5 text-[13px]">{sub}</p> : null}
          </div>
          {onClose ? (
            <button aria-label="Close" onClick={onClose} className="ui-close grid h-11 w-11 flex-none place-items-center text-xl">
              ×
            </button>
          ) : null}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-5 py-4">{children}</div>
        {footer ? <div className="ui-modal-foot flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">{footer}</div> : null}
      </div>
    </div>
  );
}

export function Keypad({ onKey, keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "⌫"], dark, height = "h-16" }: { onKey: (k: string) => void; keys?: string[]; dark?: boolean; height?: string }) {
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {keys.map((k) => {
        const digit = /^[\d.]$/.test(k);
        return (
          <button
            key={k}
            type="button"
            aria-label={k === "⌫" ? "Delete" : k === "." ? "Point" : k}
            onClick={() => onKey(k)}
            className={cx(height, digit ? "font-mono text-2xl font-semibold" : "text-base font-bold", dark ? (digit ? "bg-steel text-white hover:bg-steel-2" : "bg-graphite text-white hover:bg-steel") : "border border-line bg-white hover:border-ink")}
          >
            {k}
          </button>
        );
      })}
    </div>
  );
}

/** PIN entry boxes: filled when typed, the next one outlined. */
export function PinDots({ length, dark, size = "h-16" }: { length: number; dark?: boolean; size?: string }) {
  return (
    <div className="grid grid-cols-4 gap-2" aria-live="polite" aria-label={`${length} of 4 digits`}>
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className={cx(size, "ui-pinbox grid place-items-center border-2", dark ? "bg-graphite" : "bg-white", i === length ? (dark ? "border-brand-light" : "border-ink") : dark ? "border-steel-2" : "border-line")}>
          <span className={cx("ui-dot h-4 w-4", i < length ? (dark ? "bg-brand-light" : "bg-brand") : "bg-transparent")} />
        </span>
      ))}
    </div>
  );
}

/** Black or white text, whichever reads better on `hex`. */
export function inkOn(hex: string | undefined): string {
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex ?? "");
  if (!m) return "#101214";
  const [r, g, b] = [m[1], m[2], m[3]].map((x) => parseInt(x!, 16) / 255).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  const lum = 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
  return lum > 0.35 ? "#101214" : "#ffffff";
}

/** Apply a keypad key to a string amount ("12.5"). */
export function typeKey(v: string, k: string): string {
  if (k === "⌫") return v.slice(0, -1);
  if (k === "C" || k === "Clear") return "";
  if (k === ".") return v.includes(".") ? v : (v || "0") + ".";
  if (/^\d{1,9}(\.\d{0,1})?$/.test(v) || v === "") return v === "0" ? k : v + k;
  return v;
}

export function Avatar({ name, color, size = 32, image }: { name: string; color?: string; size?: number; image?: string | null }) {
  if (image) return <img src={mediaUrl(image, size * 2)} alt="" style={{ width: size, height: size }} className="ui-avatar object-cover" />;
  return (
    <span style={{ width: size, height: size, background: color ?? "#ffb36b", color: inkOn(color ?? "#ffb36b"), fontSize: Math.max(10, size / 2.8) }} className="ui-avatar grid flex-none place-items-center font-black">
      {initials(name)}
    </span>
  );
}

export function Thumb({ src, name, className }: { src: string | null; name: string; className?: string }) {
  const [bad, setBad] = useState(false);
  return src && !bad ? (
    <img src={mediaUrl(src, 240)} alt="" loading="lazy" onError={() => setBad(true)} className={cx("ui-thumb object-cover", className)} />
  ) : (
    <div title={name} className={cx("ui-thumb grid place-items-center overflow-hidden bg-[#ebe9e3] text-center text-[12px] font-extrabold text-muted", className)}>{initials(name)}</div>
  );
}

export function Badge({ tone = "slate", children }: { tone?: "slate" | "brand" | "amber" | "red" | "blue" | "dark" | "violet"; children: ReactNode }) {
  const t = {
    slate: "bg-[#ebe9e3] text-[#3a4048]",
    brand: "bg-brand-tint text-brand-ink",
    amber: "bg-warn-tint text-warn-ink",
    red: "bg-stop text-white",
    blue: "bg-blue-100 text-blue-900",
    dark: "bg-ink text-white",
    violet: "bg-violet-100 text-violet-900",
  }[tone];
  return <span className={cx("ui-pill inline-flex items-center px-2 py-0.5 text-[11px] font-bold", t)}>{children}</span>;
}

export function Spinner() {
  return <span className="ui-dot inline-block h-4 w-4 animate-spin border-2 border-slate-300 border-t-slate-900" />;
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
      <b className="text-base">{title}</b>
      {children ? <div className="text-sm text-slate-500">{children}</div> : null}
    </div>
  );
}

// ─────────────── toasts ───────────────
type Toast = { id: number; text: string; tone: "ok" | "err" | "info" };
const ToastCtx = createContext<{ push: (text: string, tone?: Toast["tone"]) => void }>({ push: () => {} });
export function ToastProvider({ children }: { children: ReactNode }) {
  const [list, setList] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: Toast["tone"] = "ok") => {
    const id = Date.now() + Math.random();
    setList((l) => [...l, { id, text, tone }].slice(-3));
    setTimeout(() => setList((l) => l.filter((t) => t.id !== id)), tone === "err" ? 6000 : 3200);
  }, []);
  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      <div className="pointer-events-none fixed bottom-4 left-1/2 z-[60] flex w-[min(520px,92vw)] -translate-x-1/2 flex-col gap-2">
        {list.map((t) => (
          <div key={t.id} role="status" className={cx("ui-toast px-4 py-3 text-sm font-semibold shadow-lg", t.tone === "err" ? "bg-stop text-white" : t.tone === "info" ? "bg-ink text-white" : "bg-brand text-white")}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx).push;

// ─────────────────────────── confirm dialog (instead of window.confirm) ───────────────────────────
type ConfirmReq = { title: string; body?: ReactNode; ok: string; cancel?: string; danger?: boolean };
const ConfirmCtx = createContext<(r: ConfirmReq) => Promise<boolean>>(async () => false);

/** `const confirm = useConfirm(); if (await confirm({ title, ok })) …` — an in-app dialog that keeps kiosk mode. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [req, setReq] = useState<(ConfirmReq & { done: (v: boolean) => void }) | null>(null);
  const ask = useCallback((r: ConfirmReq) => new Promise<boolean>((done) => setReq({ ...r, done })), []);
  const close = (v: boolean) => {
    req?.done(v);
    setReq(null);
  };
  return (
    <ConfirmCtx.Provider value={ask}>
      {children}
      {req ? (
        <Modal
          title={req.title}
          onClose={() => close(false)}
          footer={
            <div className="grid w-full grid-cols-2 gap-2">
              <Button size="lg" onClick={() => close(false)}>
                {req.cancel ?? "Cancel"}
              </Button>
              <Button size="lg" tone={req.danger ? "danger" : "primary"} onClick={() => close(true)} className={req.danger ? "!bg-stop !text-white" : ""} autoFocus>
                {req.ok}
              </Button>
            </div>
          }
        >
          {req.body ? <div className="text-[15px] leading-relaxed text-[#3a4048]">{req.body}</div> : null}
        </Modal>
      ) : null}
    </ConfirmCtx.Provider>
  );
}
export const useConfirm = () => useContext(ConfirmCtx);
